import { createI18n } from "vue-i18n";
import { setTranslator } from "@smeetz/i18n";
import storage, { StorageKeys } from "@/services/storage.service";

import { LanguagesType } from "@/types/app";
import { DEFAULT_LANG, SiteLanguages } from "@/utils/constants";

import enLocale from "./en.json";
import frLocale from "./fr.json";
import deLocale from "./de.json";
import itLocale from "./it.json";
import esLocale from "./es.json";
import hrLocale from "./hr.json";

import elementEnLocale from "element-plus/es/locale/lang/en";
import elementFrLocale from "element-plus/es/locale/lang/fr";
import elementDeLocale from "element-plus/es/locale/lang/de";
import elementItLocale from "element-plus/es/locale/lang/it";
import elementEsLocale from "element-plus/es/locale/lang/es";
import elementHrLocale from "element-plus/es/locale/lang/hr";

const messages = {
  en: {
    ...(enLocale as any),
    ...elementEnLocale,
  },
  fr: {
    ...(frLocale as any),
    ...elementFrLocale,
  },
  de: {
    ...(deLocale as any),
    ...elementDeLocale,
  },
  it: {
    ...(itLocale as any),
    ...elementItLocale,
  },
  es: {
    ...(esLocale as any),
    ...elementEsLocale,
  },
  // Croatian is registered so compliance labels can be resolved via a forced
  // `locale: "hr"` (see SystemInfo.vue). It is intentionally kept out of
  // SiteLanguages so it is not offered as a selectable UI language.
  hr: {
    ...(hrLocale as any),
    ...elementHrLocale,
  },
};

export const getLanguageCookies = () => storage.getItem(StorageKeys.Language);
export const setLanguageCookies = (language: string) =>
  storage.setItem(StorageKeys.Language, language);

export const getLocale = (): LanguagesType => {
  const cookieLanguage = getLanguageCookies();

  if (cookieLanguage) {
    // @ts-ignore
    return cookieLanguage;
  } else {
    let userLang: string | undefined;
    try {
      userLang = navigator.language;
    } catch (error) {
      userLang = DEFAULT_LANG;
    }

    userLang = userLang.toLowerCase();

    let language: LanguagesType = DEFAULT_LANG;

    for (const lang of SiteLanguages) {
      if (userLang.indexOf(lang) !== -1) {
        language = lang;
        return language;
      }
    }

    return language;
  }
};

export function setI18nLanguage(
  i18n: { global: { locale: { value: string } } },
  locale: string
) {
  i18n.global.locale.value = locale;
}

// @ts-ignore
const i18n = createI18n({
  locale: getLocale(), // set locale
  fallbackLocale: "en", // set fallback locale
  messages, // set locale messages

  legacy: false, // set `false` to use Composition API

  /* This allows Vue I18n to inject the following properties and functions into the components => $i18n, $t, $n ... */
  globalInjection: true, // set `true` to make a global properties and function available for all components (work together with legacy: false)

  /* To suppress the warning(Not found key...) */
  // missingWarn: false, // in Composition API mode(legacy: false)
  // silentTranslationWarn: true, // in Legacy API mode(legacy: true)

  /* To suppress the warning(Fall back to...) */
  // fallbackWarn: false // in Composition API mode(legacy: false)
  // silentFallbackWarn: true, // in Legacy API mode(legacy: true)
});

// Bridge @smeetz/i18n's global `t()` to this vue-i18n instance at bootstrap, so
// design-system and @smeetz/services-validation messages resolve from the very first screen instead of returning
// the raw key. pos-client-init re-points the same bridge after login.
setTranslator((key: string, ...args: any[]) =>
  (i18n.global as any).t(key, ...args)
);

export default i18n;
